import * as THREE from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { texture, uv, positionWorld, uniform, float, Fn, Discard, vertexColor } from 'three/tsl';
import { TILE } from '../../shared/geo.js';
import { GROUND_PX, groundColor, paintGround as paint, terrainArrays } from './ground.js';

export { groundColor };

// Paints the ground picture for one tile onto a new page canvas.
export function paintGround(data, tx, tz, heightmap) {
  const c = document.createElement('canvas');
  c.width = GROUND_PX;
  c.height = GROUND_PX;
  return paint(c, data, tx, tz, heightmap);
}

// The ground materials alive right now (one per tile), and the shared tint wetness applies to
// them: a Color multiplied in on the legacy path, a uniform node on the new renderer.
export const GROUND_MATERIALS = new Set();
const groundTint = uniform(1);
let groundTintValue = 1;

// k: 1 dry, lower when wet (dark, sodden earth).
export function setGroundTint(k) {
  if (k === groundTintValue) return;
  groundTintValue = k;
  groundTint.value = k;
  for (const m of GROUND_MATERIALS) if (!m.isNodeMaterial) m.color.setScalar(k);
}

export function disposeGround(material) {
  GROUND_MATERIALS.delete(material);
  if (material.map) material.map.dispose();
  material.dispose();
}

// One shared shader tweak adds fine detail to every ground tile.
// nodes: build it as a node material (the new renderer) instead of patching the GLSL.
export function groundMaterial(map, detail, nodes = false) {
  if (nodes) {
    const m = new MeshLambertNodeMaterial({ map });
    const at = positionWorld.xz.mul(0.11);
    const fine = texture(detail, at).r;
    const broad = texture(detail, at.mul(0.13)).r;
    m.colorNode = texture(map, uv()).mul(float(0.6).add(fine.mul(0.5)).add(broad.mul(0.3))).mul(groundTint);
    GROUND_MATERIALS.add(m);
    return m;
  }
  const m = new THREE.MeshLambertMaterial({ map });
  m.color.setScalar(groundTintValue);
  GROUND_MATERIALS.add(m);
  m.onBeforeCompile = (shader) => {
    shader.uniforms.uDetail = { value: detail };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vDetailUv;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvDetailUv = (modelMatrix * vec4(position, 1.0)).xz * 0.11;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform sampler2D uDetail;\nvarying vec2 vDetailUv;')
      .replace(
        '#include <map_fragment>',
        '#include <map_fragment>\nfloat fine = texture2D(uDetail, vDetailUv).r;\nfloat broad = texture2D(uDetail, vDetailUv * 0.13).r;\ndiffuseColor.rgb *= 0.6 + 0.5 * fine + 0.3 * broad;'
      );
  };
  m.customProgramCacheKey = () => 'abakwa-ground';
  return m;
}

// The ground mesh of one tile, in tile-local x and z with real heights in y.
export function buildTerrain(tx, tz, heightmap, groundCanvas, detail, nodes = false) {
  const map = new THREE.CanvasTexture(groundCanvas);
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 4;
  const mesh = groundMesh(terrainArrays(tx * TILE, tz * TILE, TILE, heightmap), groundMaterial(map, detail, nodes));
  mesh.position.set(tx * TILE, 0, tz * TILE);
  mesh.userData.ownsMaterial = true;
  return mesh;
}

// A ground mesh from arrays made by terrainArrays, drawn with the given material.
export function groundMesh(arrays, material) {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(arrays.pos, 3));
  geo.setAttribute('normal', new THREE.BufferAttribute(arrays.nor, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(arrays.uv, 2));
  geo.setIndex(new THREE.BufferAttribute(arrays.index, 1));
  const mesh = new THREE.Mesh(geo, material);
  mesh.receiveShadow = true;
  return mesh;
}

// A coarse mesh of the whole region so distant hills stay visible. Fragments
// inside `hole` (the area covered by detailed tiles) are not drawn.
export function buildFarTerrain(heightmap, bounds, hole, nodes = false) {
  const step = 192;
  const w = Math.floor((bounds.x1 - bounds.x0) / step) + 1;
  const h = Math.floor((bounds.z1 - bounds.z0) / step) + 1;
  const pos = new Float32Array(w * h * 3);
  const color = new Float32Array(w * h * 3);
  const col = [0, 0, 0];
  for (let j = 0; j < h; j++) {
    for (let i = 0; i < w; i++) {
      const k = j * w + i;
      const x = bounds.x0 + i * step;
      const z = bounds.z0 + j * step;
      const y = heightmap.gridHeight(x, z);
      pos[k * 3] = x;
      pos[k * 3 + 1] = y - 5;
      pos[k * 3 + 2] = z;
      const sx = (heightmap.gridHeight(x + 60, z) - heightmap.gridHeight(x - 60, z)) / 120;
      const sz = (heightmap.gridHeight(x, z + 60) - heightmap.gridHeight(x, z - 60)) / 120;
      groundColor(x, z, y, Math.hypot(sx, sz), col);
      color[k * 3] = Math.pow(col[0] / 255, 2.2) * 0.9;
      color[k * 3 + 1] = Math.pow(col[1] / 255, 2.2) * 0.9;
      color[k * 3 + 2] = Math.pow(col[2] / 255, 2.2) * 0.9;
    }
  }
  const index = new Uint32Array((w - 1) * (h - 1) * 6);
  let q = 0;
  for (let j = 0; j < h - 1; j++) {
    for (let i = 0; i < w - 1; i++) {
      const a = j * w + i;
      index[q++] = a;
      index[q++] = a + w;
      index[q++] = a + w + 1;
      index[q++] = a;
      index[q++] = a + w + 1;
      index[q++] = a + 1;
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(color, 3));
  geo.setIndex(new THREE.BufferAttribute(index, 1));
  geo.computeVertexNormals();
  let mat;
  if (nodes) {
    // the hole is the same Vector4 the chunks keep writing, read through a uniform node
    mat = new MeshLambertNodeMaterial({ vertexColors: true });
    const edge = uniform(hole.value);
    mat.colorNode = Fn(() => {
      const p = positionWorld;
      Discard(p.x.greaterThan(edge.x).and(p.z.greaterThan(edge.y)).and(p.x.lessThan(edge.z)).and(p.z.lessThan(edge.w)));
      return vertexColor();
    })();
  } else mat = new THREE.MeshLambertMaterial({ vertexColors: true });
  if (!nodes) mat.onBeforeCompile = (shader) => {
    shader.uniforms.uHole = hole;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vFlat;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFlat = position.xz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform vec4 uHole;\nvarying vec2 vFlat;')
      .replace('#include <map_fragment>', '#include <map_fragment>\nif (vFlat.x > uHole.x && vFlat.y > uHole.y && vFlat.x < uHole.z && vFlat.y < uHole.w) discard;');
  };
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  return mesh;
}
